import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { sectionPath } from "@/components/job/Lifecycle";
import { approveTrial } from "@/components/assessment/approve";

export const jobIdOf = (url) => url?.match(/^\/recruiter\/jobs\/([^/?#]+)\/build/)?.[1];

// null = loading, false = could not load.
export function useSessionJob(jobId, rev, busy) {
  const [job, setJob] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!jobId) { setJob(null); return; }
    let alive = true;
    api.get(`/jobs/${jobId}`).then(({ data }) => { if (alive) setJob(data); }).catch(() => { if (alive) setJob(false); });
    return () => { alive = false; };
  }, [jobId, rev, busy, tick]);
  return { job, refresh: () => setTick((t) => t + 1) };
}

const copy = async (url, msg) => {
  try { await navigator.clipboard.writeText(url); toast.success(msg); } catch { toast.message("Link", { description: url }); }
};

export function useJourneyActions(job, sessionId, send, refresh, busy) {
  const nav = useNavigate();
  const [working, setWorking] = useState("");
  const [asking, setAsking] = useState(false);
  const lc = job?.lifecycle || {};
  const back = `?return=${encodeURIComponent(`/copilot/${sessionId}`)}`;
  const run = async (key, fn) => {
    setWorking(key);
    try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || "That didn't work. Please try again."); } finally { setWorking(""); }
  };
  return {
    working, busy, refresh, asking, setAsking,
    open: (url) => nav(url),
    ask: () => setAsking(true),
    approve: () => run("approve", async () => { if (await approveTrial(lc.latest_trial_id, { publish: true, title: job.title })) refresh(); }),
    generate: () => send("Generate the assessment for this role", "generate_assessment"),
    trial: () => run("trial", async () => {
      const { data } = await api.post(`/jobs/${job.id}/test/trial`, lc.latest_version ? { version: lc.latest_version } : {});
      nav(`/recruiter/jobs/${job.id}/trial/${data.id}${back}`);
    }),
    report: () => nav(`/recruiter/trial-report/${lc.latest_trial_id}${back}`),
    publish: () => run("publish", async () => {
      if (lc.latest_version && !lc.active_version) await api.post(`/jobs/${job.id}/test/${lc.latest_version}/publish`);
      await api.post(`/jobs/${job.id}/publish`);
      toast.success("Your role is live on the careers page");
      refresh();
    }),
    copyPublic: () => copy(`${window.location.origin}/careers/${job.id}`, "Public link copied"),
  };
}

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

// Anyone on the team can approve, including whoever ran the trial; a teammate's review is optional.
function signoffStep(lc, a) {
  const waiting = lc.signoff_waiting || [];
  const why = waiting.length ? `Waiting on ${waiting.join(", ")}. You can also approve it yourself.`
    : "Look over the questions, then approve the trial report. That publishes the role on your careers page. Want a second look first? Ask a teammate.";
  return { section: "test", title: "Approve and publish", why, primary: { key: "approve", label: "Approve and publish", run: a.approve }, actions: [{ label: "Ask a teammate", run: a.ask }, { label: "Open trial report", run: a.report }] };
}

// The single "what now?" answer for a role, derived from the backend lifecycle.
export function journeyNext(job, a) {
  const lc = job.lifecycle || {};
  const id = job.id;
  const live = job.status === "published";
  const edit = { label: "Edit the role", run: () => a.open(sectionPath(id, "build")) };
  const review = { label: "Review questions", run: () => a.open(`${sectionPath(id, "test")}?stage=questions`) };
  const board = { label: "Open candidate board", run: () => a.open(`/recruiter/pipeline?job=${id}`) };
  if (!lc.role_complete) return { section: "build", title: "Finish the role details", why: "Answer the questions above or tell me what to change. A role needs a title, a summary and scoring competencies before it can be assessed.", actions: [edit] };
  if (!live && !lc.has_test) return { section: "test", title: "Build the assessment", why: "Every applicant takes the same fair, job-relevant test, scored against this role's competencies. I'll draft it from the role; you review it before anything goes live.", primary: { key: "generate", label: "Generate assessment", run: a.generate }, actions: [{ label: "Set the blueprint first", run: () => a.open(sectionPath(id, "test")) }] };
  if (!live && !lc.has_trial) return { section: "test", title: "Try the assessment as a candidate", why: "A trial run shows exactly what applicants will see and produces a quality report for your team to approve.", primary: { key: "trial", label: "Start trial run", run: a.trial }, actions: [review] };
  if (!live && !lc.signed_off) return signoffStep(lc, a);
  if (!live) return { section: "publish", title: "Publish the role", why: `Signed off and ready. Publishing puts it on your careers page${!lc.active_version && lc.latest_version ? ` and makes assessment v${lc.latest_version} the one candidates take` : ""}.`, primary: { key: "publish", label: "Publish role", run: a.publish }, actions: [{ label: "See publishing options", run: () => a.open(sectionPath(id, "publish")) }] };
  if (lc.counts?.offer || lc.counts?.hired) return { title: lc.counts.hired ? "Role filled" : `${plural(lc.counts.offer, "offer")} out`, why: "Track responses and close out the remaining applicants on the board.", primary: { key: "board", ...board }, actions: [] };
  if (lc.counts?.interview) return { title: `${plural(lc.counts.interview, "candidate")} in interview`, why: "Schedule panels and record debriefs; the board updates as you go.", primary: { key: "interviews", label: "Open interviews", run: () => a.open("/recruiter/interviews") }, actions: [board] };
  if (lc.to_review) return { title: `Review ${plural(lc.to_review, "applicant")}`, why: "Each applicant is already scored against your competencies, with evidence. Shortlist, reject or move them to interview.", primary: { key: "board", ...board }, actions: [{ label: "Copy public link", run: a.copyPublic }] };
  return { title: "Your role is live", why: "Share the link to start getting applicants. Each one is scored against your competencies as they finish the assessment.", primary: { key: "copy", label: "Copy public link", run: a.copyPublic }, actions: [board] };
}
